import { Component, OnInit, computed, effect, inject, Signal, ChangeDetectionStrategy } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { map } from 'rxjs/operators';
import { SettingsService } from 'app/shared/services/admin-settings.service';
import { CurrentContextService } from 'app/core/current-context.service';
import { EarlyAccessFeatures } from 'app/core/identity/user-identity';
import { UserUiPreferencesService } from 'app/core/user-ui-preferences.service';
import { TopLevelSideMenuService } from './top-level-side-menu.service';
import {
  CreateTarget,
  FeatureFlagState,
  resolveCreateTarget,
  SubNavItem,
  TOP_LEVEL_NAV,
  TopLevelNavItem,
} from '../navigation.config';
import { AppExperiencesService } from 'app/shared/services/app-experiences.service';

@Component({
  selector: 'app-top-level-side-menu',
  templateUrl: './top-level-side-menu.component.html',
  styleUrls: ['./top-level-side-menu.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class TopLevelSideMenuComponent implements OnInit {
  private readonly menuService = inject(TopLevelSideMenuService);
  private readonly settingsService = inject(SettingsService);
  private readonly currentContextService = inject(CurrentContextService);
  private readonly uiPreferencesService = inject(UserUiPreferencesService);
  private readonly appExperiencesService = inject(AppExperiencesService);

  private readonly earlyAccessFeatures = toSignal(
    this.currentContextService.currentContextBS.pipe(map((context) => context?.user?.earlyAccessFeatures ?? [])),
    { initialValue: [] as EarlyAccessFeatures[] },
  );

  // URLs of every entry flagged as "new", a section or a sub-item, used to mark the dot as seen once visited.
  private readonly newItemUrls: readonly string[] = TOP_LEVEL_NAV.flatMap((top) => [
    ...(top.newTag ? [top.url] : []),
    ...top.subItems
      .filter((sub): sub is SubNavItem => !!(sub as SubNavItem).newTag && !!(sub as SubNavItem).url)
      .map((sub) => sub.url as string),
  ]);
  // Read straight from the service, so a sign-out and the next user's load reach the menu without a reload.
  private readonly seenNewItems = computed<readonly string[]>(
    () => this.uiPreferencesService.get('sideMenu')?.seenNewItems ?? [],
  );
  private readonly preferencesLoaded = this.uiPreferencesService.loaded;

  constructor() {
    // Once the user opens a "new" route, remember it so the notification dot stays hidden.
    effect(() => {
      // Writing before the stored list arrives would save only this url and overwrite the rest.
      if (!this.preferencesLoaded()) {
        return;
      }
      const path = this.menuService.currentUrl().split('?')[0];
      // Skip urls already seen, or a nested "new" route would keep matching its seen parent.
      const seen = this.seenNewItems();
      const match = this.newItemUrls.find((url) => !seen.includes(url) && (path === url || path.startsWith(url + '/')));
      if (match) {
        this.markNewItemSeen(match);
      }
    });
  }

  ngOnInit(): void {
    void this.uiPreferencesService.load();
  }

  protected readonly expanded = this.menuService.expanded;
  protected readonly pinned = this.menuService.pinned;
  protected readonly activeTopLevel = this.menuService.activeTopLevel;

  private readonly settings = toSignal(this.settingsService.settings$, {
    initialValue: this.settingsService.getCurrentSettings(),
  });
  private readonly adminToggle = computed(() => this.settings()?.adminToggle ?? false);

  private readonly featureFlags = computed<FeatureFlagState>(() => ({
    appExperiences: this.appExperiencesService.enabled(),
  }));

  private readonly isBackofficeUrl = computed(() =>
    this.menuService.currentUrl().split('?')[0].startsWith('/backoffice'),
  );

  protected readonly items: Signal<TopLevelNavItem[]> = computed(() => {
    const backoffice = this.isBackofficeUrl();
    const featureFlags = this.featureFlags();
    return TOP_LEVEL_NAV.filter((item) => {
      if (item.hidden) return false;
      // Mobile-only sections (e.g. "My Triggerbee") never appear in the desktop rail.
      if (item.mobileOnly) return false;
      if (item.adminOnly && !this.adminToggle()) return false;
      if (backoffice ? !item.backofficeOnly : !!item.backofficeOnly) return false;
      if (item.featureFlag && !featureFlags[item.featureFlag]) return false;
      return true;
    });
  });

  protected readonly createAction = computed<CreateTarget | null>(() =>
    resolveCreateTarget(this.menuService.currentUrl().split('?')[0]),
  );

  protected onMouseEnter(): void {
    this.menuService.setHovered(true);
  }

  protected onMouseLeave(): void {
    this.menuService.setHovered(false);
  }

  protected togglePin(): void {
    this.menuService.togglePin();
  }

  protected onItemClick(item: TopLevelNavItem, event: Event): void {
    this.menuService.selectTopLevel(item.id);
    if (!this.hasNavigableUrl(item)) {
      event.preventDefault();
    }
  }

  protected isActive(item: TopLevelNavItem): boolean {
    return this.activeTopLevel()?.id === item.id;
  }

  protected hasNewBadge(item: TopLevelNavItem): boolean {
    // Until the seen list arrives every item looks unseen, so hide the dot rather than flash it.
    if (!this.preferencesLoaded()) {
      return false;
    }
    const granted = this.earlyAccessFeatures();
    const seen = this.seenNewItems();
    if (item.newTag && !seen.includes(item.url)) {
      return true;
    }
    return item.subItems.some((sub) => {
      const navItem = sub as SubNavItem;
      if (!navItem.newTag) {
        return false;
      }
      if (navItem.earlyAccessFeature && !granted.includes(navItem.earlyAccessFeature)) {
        return false;
      }
      return !navItem.url || !seen.includes(navItem.url);
    });
  }

  private hasNavigableUrl(item: TopLevelNavItem): boolean {
    return !!item.url && item.url !== '#';
  }

  // Persist the "seen" state on the user, not the account, so it follows them and isn't shared with colleagues.
  private markNewItemSeen(url: string): void {
    this.uiPreferencesService.update('sideMenu', {
      ...this.uiPreferencesService.get('sideMenu'),
      seenNewItems: [...this.seenNewItems(), url],
    });
  }
}
